import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PRIMARY_CTA_LABEL, SECONDARY_CTA_LABEL } from '@/lib/homeData';
import { getExperimentVariant } from '@/lib/experiments';
import { logFirebaseEvent } from '@/lib/tracking';
import { HeroDealFlowMockup } from '@/components/home/FintechMockups';

interface HeroSectionProps {
  appSignUpUrl: string;
}

export default function HeroSection({ appSignUpUrl }: HeroSectionProps) {
  const [primaryVariant, setPrimaryVariant] = useState<'A' | 'B'>('A');

  useEffect(() => {
    const variant = getExperimentVariant('home_hero_primary_cta', ['A', 'B']);
    setPrimaryVariant(variant === 'B' ? 'B' : 'A');
  }, []);

  const primaryLabel = primaryVariant === 'B' ? 'See Financing in Action' : PRIMARY_CTA_LABEL;

  return (
    <section className="relative overflow-hidden bg-[#f8f5ef] px-6 pb-16 pt-22 md:pb-20 md:pt-28">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-[radial-gradient(circle_at_18%_100%,rgba(16,185,129,0.12),transparent_42%),radial-gradient(circle_at_78%_10%,rgba(212,175,55,0.12),transparent_42%),linear-gradient(180deg,#020617_0%,#081331_100%)] md:h-32"
      />
      <div className="pointer-events-none absolute inset-0 -z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.08 }}
          className="absolute -right-20 top-16 h-80 w-80 rounded-full bg-amber-200/35 blur-3xl"
        />
      </div>

      <div className="container mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-14">
        <div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <Badge className="rounded-full border border-slate-200 bg-white/95 px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-slate-700 shadow-sm">
              Powered by Stripe Connect
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="mt-6 max-w-[11.5ch] text-4xl font-extrabold leading-[0.94] tracking-[-0.045em] text-slate-950 md:text-[3.85rem]"
          >
            Don&apos;t let sticker shock kill your deals.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.12 }}
            className="mt-6 max-w-[34rem] text-[1.08rem] leading-[1.65] tracking-[-0.01em] text-slate-600"
          >
            Generate a financing link in 15 seconds. Let buyers pay over time with Klarna, Affirm, or Afterpay while you get paid upfront.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Button
              asChild
              size="lg"
              className="h-12 rounded-xl bg-emerald-600 px-6 text-white shadow-[0_12px_24px_-14px_rgba(5,150,105,0.8)] hover:scale-[1.01] hover:bg-emerald-500 hover:shadow-[0_18px_28px_-16px_rgba(5,150,105,0.9)] focus-visible:ring-emerald-300"
            >
              <a
                href={appSignUpUrl}
                data-exp-slot="home_hero_primary_cta"
                data-exp-variant={primaryVariant}
                onClick={() => {
                  logFirebaseEvent('marketing_cta_click', {
                    slot: 'home_hero_primary_cta',
                    variant: primaryVariant,
                    label: primaryLabel,
                  });
                }}
              >
                {primaryLabel}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 rounded-xl border-slate-300 bg-white px-6 text-slate-800 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 hover:shadow-sm focus-visible:ring-slate-300"
            >
              <Link to="/features">
                {SECONDARY_CTA_LABEL}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.16 }}
          className="relative flex items-center justify-center"
        >
          <HeroDealFlowMockup />
        </motion.div>
      </div>
    </section>
  );
}
